import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Dispatch } from "redux";
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../../../reducer/loginSlice';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { PortalApis } from '../../../apis/PortalApis';
import { SvgElement, icontypesEnum } from "../../assets/svgElement";

function ApiAccessRegister() {

    const [accountType, setAccountType] = useState<string>('partner');

    const [userData, setUserdata] = useState({
        'company_name': "",
        'contact_name': "",
        'contact_email': "",
        'phone_number': "",
        'password': "",
        'confirm_password': "",
        'website': "",
        'business_description': "",
        'expected_monthly_volume': "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setUserdata({ ...userData, [name]: value });
    };

    const [toggle, setToggle] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch: Dispatch = useDispatch();

    const handleSubmit = React.useCallback(
        (e: React.ChangeEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (userData?.password !== userData?.confirm_password) {
                return toast.error("Password does not match");
            }

            if (userData?.password.length < 8) {
                return toast.error("Password must be at least 8 characters");
            }

            setToggle(true);

            const formData = new FormData();
            formData.append('account_type', accountType);
            formData.append('company_name', userData?.company_name);
            formData.append('contact_name', userData?.contact_name);
            formData.append('contact_email', userData?.contact_email);
            formData.append('phone_number', userData?.phone_number);
            formData.append('password', userData?.password);
            formData.append('password_confirmation', userData?.confirm_password);
            formData.append('website', userData?.website);
            formData.append('business_description', userData?.business_description);
            formData.append('expected_monthly_volume', userData?.expected_monthly_volume);

            PortalApis.register(formData).then(
                (response: any) => {
                    if (response?.data) {
                        if (response?.data?.status === true || response?.data?.success) {
                            toast.success(response?.data?.message || "Registration Successful! Your application is under review.");
                            setTimeout(() => navigate('/api-access/login'), 2000);
                            setToggle(false);
                        } else {
                            toast.warn(response?.data?.message || 'Registration failed');
                            setToggle(false);
                        }
                    } else {
                        toast.warn('Something went wrong, Try again.');
                        setToggle(false);
                    }
                }
            ).catch(function (error) {
                const msg = error?.response?.data?.message;
                if (typeof msg === 'object') {
                    Object.values(msg).forEach((m: any) => toast.error(Array.isArray(m) ? m[0] : m));
                } else {
                    toast.warn(msg || "Registration failed");
                }
                setToggle(false);
            }).finally(() => {
                setToggle(false);
            });
        },
        [userData, accountType]
    );

    return (
        <>
            <div className="flex flex-row">
                <div className="lg:basis-7/12 basis-full relative">
                    <div className="min-h-screen bg-white lg:pl-[90px] lg:pr-[60px] pl-[30px] pr-[20px] pt-[70px] lg:pb-[40px]">
                        <div className="flex justify-between">
                            <NavLink to='/'>
                                <img src="/images/Auth/logo.png" alt="hero" />
                            </NavLink>
                            <NavLink to={`/api-access/login`}>
                                <div className="flex space-x-2">
                                    <h3 className="text-[#E04343] lg:text-[14px] text-[12px]">Already have an API account?</h3>
                                    <svg width="18" height="16" className="mt-1" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.15 0C11.4705 0 13.35 1.79 13.35 4C13.35 6.21 11.4705 8 9.15 8C6.8295 8 4.95 6.21 4.95 4C4.95 1.79 6.8295 0 9.15 0ZM9.15 16C9.15 16 17.55 16 17.55 14C17.55 11.6 13.455 9 9.15 9C4.845 9 0.75 11.6 0.75 14C0.75 16 9.15 16 9.15 16Z" fill="#E04343" />
                                    </svg>
                                </div>
                            </NavLink>
                        </div>

                        <div className="mt-[20px] flex lg:flex-row flex-col lg:justify-between lg:gap-14">
                            <h1 className="text-[#42505C] lg:text-[54px] text-[44px] font-[700]">API Access</h1>
                            <p className="text-[#404040] lg:text-[14px] text-[12px] font-[400] mt-6 leading-[28px]">Register to integrate with our shipping API. Your application will be reviewed by our team. Once approved, you'll receive your API credentials.</p>
                        </div>

                        {/* Account Type Toggle */}
                        <div className="mt-6 flex gap-3">
                            <button type="button" onClick={() => setAccountType('partner')}
                                className={`flex-1 py-3 rounded-[10px] text-[14px] font-[600] border-2 transition ${accountType === 'partner' ? 'bg-[#E04343] text-white border-[#E04343]' : 'bg-white text-[#808080] border-[#D1D1D1] hover:border-[#E04343]'}`}>
                                Partner (Company)
                                <span className="text-[11px] font-[400] block opacity-80 mt-0.5">For businesses integrating via API</span>
                            </button>
                            <button type="button" onClick={() => setAccountType('customer')}
                                className={`flex-1 py-3 rounded-[10px] text-[14px] font-[600] border-2 transition ${accountType === 'customer' ? 'bg-[#E04343] text-white border-[#E04343]' : 'bg-white text-[#808080] border-[#D1D1D1] hover:border-[#E04343]'}`}>
                                Customer (Individual)
                                <span className="text-[11px] font-[400] block opacity-80 mt-0.5">For existing customers wanting API</span>
                            </button>
                        </div>

                        <div className="mt-6">
                            <form onSubmit={handleSubmit}>
                                <div className="flex flex-col lg:flex-row lg:justify-between max-w-screen-xl mx-auto">
                                    <div className="relative flex flex-col min-w-0 break-words w-full">
                                        <div className="flex-auto py-10 pt-0">
                                            <div className="flex flex-wrap">

                                                <div className="lg:w-6/12 w-full lg:pr-4">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px]">{accountType === 'partner' ? 'Company Name' : 'Full Name'}</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="text" name="company_name" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder={accountType === 'partner' ? 'Acme Logistics Ltd' : 'John Smith'} />
                                                    </div>
                                                </div>

                                                <div className="lg:w-6/12 w-full">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px]">Contact Person</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="text" name="contact_name" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder="John Smith" />
                                                    </div>
                                                </div>

                                                <div className="lg:w-6/12 w-full lg:pr-4">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px] font-[400]">Email</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="email" name="contact_email" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder="api@company.com" />
                                                    </div>
                                                </div>

                                                <div className="lg:w-6/12 w-full">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px] font-[400]">Phone Number</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="number" name="phone_number" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder="+234..." />
                                                    </div>
                                                </div>

                                                {accountType === 'partner' && (
                                                    <>
                                                        <div className="lg:w-6/12 w-full lg:pr-4">
                                                            <div className="flex justify-between mb-3">
                                                                <label className="text-[#333333] text-[12px] font-[400]">Website (Optional)</label>
                                                                <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                            </div>
                                                            <div className="relative mb-4">
                                                                <input type="url" name="website" onChange={handleChange}
                                                                    className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                                    placeholder="https://company.com" />
                                                            </div>
                                                        </div>

                                                        <div className="lg:w-6/12 w-full">
                                                            <div className="flex justify-between mb-3">
                                                                <label className="text-[#333333] text-[12px] font-[400]">Expected Monthly Shipments</label>
                                                                <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                            </div>
                                                            <div className="relative mb-4">
                                                                <select name="expected_monthly_volume" onChange={handleChange}
                                                                    className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5">
                                                                    <option value="">--Select range--</option>
                                                                    <option value="1-100">1 - 100</option>
                                                                    <option value="100-500">100 - 500</option>
                                                                    <option value="500-1000">500 - 1,000</option>
                                                                    <option value="1000-5000">1,000 - 5,000</option>
                                                                    <option value="5000+">5,000+</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}

                                                <div className="lg:w-12/12 w-full lg:pr-4">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px] font-[400]">{accountType === 'partner' ? 'Business Description' : 'How will you use the API?'}</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <textarea name="business_description" required onChange={handleChange as any} rows={2}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder={accountType === 'partner' ? 'Describe your business and how you plan to use the API...' : 'Describe how you plan to use the API...'} />
                                                    </div>
                                                </div>

                                                <div className="lg:w-6/12 w-full lg:pr-4">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px] font-[400]">Password</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="password" name="password" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder="Min 8 characters" />
                                                    </div>
                                                </div>

                                                <div className="lg:w-6/12 w-full">
                                                    <div className="flex justify-between mb-3">
                                                        <label className="text-[#333333] text-[12px] font-[400]">Confirm Password</label>
                                                        <SvgElement type={icontypesEnum.QUESTIONMARK} />
                                                    </div>
                                                    <div className="relative mb-4">
                                                        <input type="password" name="confirm_password" required onChange={handleChange}
                                                            className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                                            placeholder="" />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Info Banner */}
                                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3 mb-4">
                                    <p className="text-[12px] text-blue-800">
                                        <strong>What happens next?</strong> After registration, our team will review your application.
                                        Once approved, you'll receive your API credentials via email and can access them from your portal dashboard.
                                    </p>
                                </div>

                                <div className="flex justify-between pb-8">
                                    <div className="flex items-center mb-2 mt-4">
                                        <input id="default-checkbox" type="checkbox" required className="w-4 h-4 text-blue-600 bg-gray-100 border-[#D1D1D1] rounded" />
                                        <label htmlFor="default-checkbox" className="ml-3 text-sm font-medium text-[#333333]">I agree with <span className="text-[#E04343]">API terms and conditions</span></label>
                                    </div>
                                    <div className="mt-1">
                                        <button type="submit" disabled={toggle} className="text-white bg-[#E04343] rounded-[30px] transition ease-out delay-300 py-[10px] px-[42px] font-[700] text-[14px]">
                                            <span className="flex justify-center gap-3">
                                                <span>Register</span>
                                                <span>
                                                    <Oval visible={toggle} height="20" width="20" color="#E04343" secondaryColor="#E6F1FC" ariaLabel="oval-loading" />
                                                </span>
                                            </span>
                                        </button>
                                    </div>
                                </div>

                            </form>
                        </div>

                    </div>
                </div>

                <div className="basis-5/12 hidden lg:block">
                    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-12">
                        <div className="text-center">
                            <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-4">
                                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#E04343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 17L12 22L22 17" stroke="#E04343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 12L12 17L22 12" stroke="#E04343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <h2 className="text-[24px] font-[700] text-[#263238] mb-2">Skynet API</h2>
                                <p className="text-[14px] text-[#808080]">Integrate shipment management into your platform</p>
                            </div>
                            <div className="text-left space-y-3">
                                {[
                                    { title: 'Create Shipments', desc: 'Programmatically create and manage shipments' },
                                    { title: 'Real-time Tracking', desc: 'Track shipments via NIPOST integration' },
                                    { title: 'Webhook Notifications', desc: 'Get real-time status updates via webhooks' },
                                    { title: 'Rate Calculation', desc: 'Calculate shipping rates in real-time' },
                                ].map((item) => (
                                    <div key={item.title} className="flex items-start gap-3 bg-white rounded-lg p-3 shadow-sm">
                                        <span className="bg-green-100 text-green-600 rounded-full px-1.5 py-0.5 text-[12px] mt-0.5">&#10003;</span>
                                        <div>
                                            <p className="text-[13px] font-[600] text-[#263238]">{item.title}</p>
                                            <p className="text-[11px] text-[#808080]">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </>
    );
}

export default ApiAccessRegister;